import queryString from 'query-string';
import { ProductType } from '@tink/core/src/types';
import { TinkLinkUrlParameter } from '@tink/config/src/TinkLinkUrlParameter';
import { validateRedirectURI } from '@tink/core/src/utils';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { FinancialServicesSegment } from '../models/provider';
import { validateParams as validateParamsAccountCheck } from '../banking/account-check/urlParams';
import { validateParams as validateParamsBusinessAccountCheck } from '../banking/business-account-check/urlParams';
import { validateParams as validateParamsReports } from '../reports/urlParams';
import { validateParams as validateParamsIncomeCheck } from '../banking/income-check/urlParams';
import { validateParams as validateParamsRiskInsights } from '../risk-insights/urlParams';
import { validateParams as validateParamsProducts } from '../products/urlParams';
import { validateParams as validateParamsAuthorize } from '../authorize/urlParams';
import { validateParams as validateParamsVrpMandate } from '../payments/VRP/components/VrpMandateDirector/urlParams';
import { validateParams as validateParamsPayments } from '../payments/urlParams';
import { validateParams as validateParamsTransactions } from '../banking/transactions/urlParams';
import { validateParams as validateParamsBusinessTransactions } from '../banking/business-transactions/urlParams';
import { validateParams as validateParamsExtendConsent } from '../extend-consent/urlParams';
import { validateParams as validateParamsCredentials } from '../components/CredentialsDirector/urlParams';
import { CommonError } from '../stores/app-state-errors';
import {
  ExtendConsentProduct,
  ProductDimension,
  ProductDimensionExtendConsent,
  ProductDimensionPay,
} from '../models/product';
import {
  getValidatedLocale,
  getValidatedMarket,
} from '../components/ApplicationProviders/UrlQueryProvider/queryValidator';
import { uuid, validateString } from './utils';
import { storage as availableStorage } from './storage';
import { isEnumType, ValidatedCommonUrlParameters, ValidatedUrlParameters } from './urlParameters';

export const TRACKING_ID =
  new URLSearchParams(window.location.search).get(TinkLinkUrlParameter.TRACKING_ID) || uuid();

const SENSITIVE_QUERY_PARAMETER_KEYS = ['input_username'];

export const SESSION_PROPS_KEY = 'props';

// Very arbitrary regex. Could really be anything and it depends on the given bank.
const arbitraryRegex = /^.{1,50}$/;
const arbitraryLongRegex = /^.{1,100}$/;

const getParameterFromQuery = (parameterName: string): undefined | string => {
  const parsed = queryString.parse(window.location.search);
  const result = parsed[parameterName];

  if (Array.isArray(result)) {
    return result[0];
  }

  return result || undefined;
};

export const getParameterFromStorage = (
  parameterName: string,
  storage: Storage = availableStorage
): undefined | string => {
  const sensitiveUserInformationId = getParameterFromQuery('sui_id');

  if (!sensitiveUserInformationId) {
    return undefined;
  }

  const paramsJson = storage.getItem(SESSION_PROPS_KEY);

  if (!paramsJson) {
    return undefined;
  }

  try {
    const params = JSON.parse(paramsJson);

    return params[sensitiveUserInformationId][parameterName];
  } catch {
    return undefined;
  }
};

export const getParameter = (parameterName: TinkLinkUrlParameter): undefined | string => {
  if (SENSITIVE_QUERY_PARAMETER_KEYS.includes(parameterName)) {
    return getParameterFromStorage(parameterName);
  }

  return getParameterFromQuery(parameterName);
};

export const validateQueryParameter = (
  name: TinkLinkUrlParameter,
  regex: RegExp
): undefined | string => {
  return validateString(getParameter(name), regex);
};

// This credential id is present in url when user authenticated on 3rd party
// provider page and is redirected back to Tink Link.
const getThirdPartyCredentialsId = (): undefined | string => {
  return getParameter(TinkLinkUrlParameter.THIRD_PARTY_CREDENTIALS_ID);
};

type GetCommonParametersProps = {
  productType: ProductType;
  scope?: string;
};

export const getInvalidFinancialServicesSegments = (
  financialServicesSegmentsUrlParameter: string | undefined
) => {
  const invalidSegments: string[] = [];

  if (!financialServicesSegmentsUrlParameter) {
    return invalidSegments;
  }

  const parsedFinancialServicesSegments = financialServicesSegmentsUrlParameter.split(',');

  parsedFinancialServicesSegments.forEach((segment) => {
    if (!Object.values(FinancialServicesSegment).includes(segment as FinancialServicesSegment)) {
      invalidSegments.push(segment);
    }
  });

  return invalidSegments;
};

export const parseOptionalBoolean = (param: TinkLinkUrlParameter) => {
  const stringBooleanOrUndefined = validateQueryParameter(param, /^(true|false)$/);
  let parsedParam;

  if (stringBooleanOrUndefined) {
    parsedParam = stringBooleanOrUndefined === 'true';
  }

  return parsedParam;
};

export const getCommonParameters = ({
  productType,
  scope,
}: GetCommonParametersProps): ValidatedCommonUrlParameters => {
  const errors: CommonError[] = [];

  const clientId = getParameter(TinkLinkUrlParameter.CLIENT_ID);
  const redirectUri = getParameter(TinkLinkUrlParameter.REDIRECT_URI);
  const state = getParameter(TinkLinkUrlParameter.STATE);
  const locale = getValidatedLocale(getParameter(TinkLinkUrlParameter.LOCALE));
  const market = getValidatedMarket(getParameter(TinkLinkUrlParameter.MARKET));
  const callbackUri = getParameter(TinkLinkUrlParameter.CALLBACK_URI);
  const authorizationToken = getParameter(TinkLinkUrlParameter.AUTHORIZATION_TOKEN);
  const credentialsId = getParameter(TinkLinkUrlParameter.USER_CREDENTIALS_ID);
  const isMobileSDK = Boolean(validateQueryParameter(TinkLinkUrlParameter.MOBILESDK, /^true$/));
  const isIframeParameter = Boolean(validateQueryParameter(TinkLinkUrlParameter.IFRAME, /^true$/));
  const appUri = getParameter(TinkLinkUrlParameter.APP_URI);

  if (!validateRedirectURI(redirectUri as string)) {
    errors.push({
      errorStatus: ErrorStatus.BAD_REQUEST,
      errorReason: ErrorReason.INVALID_PARAMETER_REDIRECT_URI,
    });
  }

  if (!clientId) {
    errors.push({
      errorStatus: ErrorStatus.BAD_REQUEST,
      errorReason: ErrorReason.INVALID_PARAMETER_CLIENT_ID,
    });
  }

  const username = getParameter(TinkLinkUrlParameter.INPUT_USERNAME);
  const isValid = validateString(username, arbitraryRegex);

  if (username && !isValid) {
    errors.push({
      errorStatus: ErrorStatus.BAD_REQUEST,
      errorReason: ErrorReason.INVALID_PARAMETER_INPUT_USERNAME,
    });
  }

  const providerName = validateQueryParameter(
    TinkLinkUrlParameter.INPUT_PROVIDER,
    arbitraryLongRegex
  );

  if (getParameter(TinkLinkUrlParameter.INPUT_PROVIDER) && !providerName) {
    errors.push({
      errorStatus: ErrorStatus.BAD_REQUEST,
      errorReason: ErrorReason.INVALID_PARAMETER_INPUT_PROVIDER,
    });
  }

  const autoRedirectMobile = Boolean(
    validateQueryParameter(TinkLinkUrlParameter.AUTO_REDIRECT_MOBILE, /^true$/i)
  );

  const credentialsIdFromTPP = getThirdPartyCredentialsId();

  const authorizationCode = validateQueryParameter(
    TinkLinkUrlParameter.AUTHORIZATION_CODE,
    arbitraryRegex
  );

  const financialInstitutionId = getParameter(TinkLinkUrlParameter.FINANCIAL_INSTITUTION_ID);

  const financialServicesSegments = getParameter(TinkLinkUrlParameter.FINANCIAL_SERVICES_SEGMENTS);
  const invalidSegments = getInvalidFinancialServicesSegments(financialServicesSegments);
  const validFinancialServicesSegments = financialServicesSegments
    ? financialServicesSegments.split(',').filter(isEnumType(FinancialServicesSegment))
    : undefined;

  if (invalidSegments.length > 0) {
    errors.push({
      errorStatus: ErrorStatus.BAD_REQUEST,
      errorReason: ErrorReason.INVALID_PARAMETER_FINANCIAL_SERVICES_SEGMENTS,
    });
  }

  const handoffSessionId = getParameter(TinkLinkUrlParameter.HANDOFF_SESSION_ID);

  const consentId = getParameter(TinkLinkUrlParameter.CONSENT_ID);

  const authenticate = Boolean(validateQueryParameter(TinkLinkUrlParameter.AUTHENTICATE, /^true$/));

  const externalReference = getParameter(TinkLinkUrlParameter.EXTERNAL_REFERENCE);

  const engineVersion = getParameter(TinkLinkUrlParameter.ENGINE_VERSION);

  const async = Boolean(validateQueryParameter(TinkLinkUrlParameter.ASYNC, /^true$/));

  // Only applicable to `/credentials/refresh`
  const optIn = parseOptionalBoolean(TinkLinkUrlParameter.OPT_IN);

  const customerReferenceId = getParameter(TinkLinkUrlParameter.CUSTOMER_REFERENCE_ID);

  const sessionId = getParameter(TinkLinkUrlParameter.SESSION_ID);
  const connectedProviders = getParameter(TinkLinkUrlParameter.CONNECTED_PROVIDERS);

  const parameters = {
    isMobileSDK,
    isIframe: isIframeParameter, // If this is true, we will not redirect but instead use the postMessage to the parent.
    clientId,
    redirectUri,
    state,
    scope,
    providerName,
    username,
    credentialsId,
    productType,
    authorizationCode,
    authorizationToken,
    credentialsIdFromTPP,
    trackingId: TRACKING_ID,
    authenticate,
    handoffSessionId,
    consentId,
    appUri,
    financialInstitutionId,
    externalReference,
    engineVersion,
    financialServicesSegments: validFinancialServicesSegments,
    customerReferenceId,
    sessionId,
    autoRedirectMobile,
    async,
    locale,
    market,
    callbackUri,
    optIn,
    connectedProviders,
  };

  return {
    errors,
    parameters,
  };
};

const getValidatedParametersForAuthorize = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.AUTHORIZE,
    scope: getParameter(TinkLinkUrlParameter.SCOPE),
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsAuthorize(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForPayment = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.PAY,
    scope: getParameter(TinkLinkUrlParameter.SCOPE),
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsPayments(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForCredentials = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.CREDENTIALS,
    scope: getParameter(TinkLinkUrlParameter.SCOPE),
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsCredentials(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForVrpMandate = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.PAY,
    scope: getParameter(TinkLinkUrlParameter.SCOPE),
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsVrpMandate(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForBusinessAccountCheck = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.BUSINESS_ACCOUNT_CHECK,
  });

  const { parameters: specificParameters, errors: specificErrors } =
    validateParamsBusinessAccountCheck(commonParameters, getParameter);

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

export const getValidatedParametersForAccountCheck = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.ACCOUNT_CHECK,
    scope: 'accounts:read',
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsAccountCheck(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

export const getValidatedParametersForReports = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.REPORTS,
    scope: 'accounts:read',
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsReports(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForTransactions = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.TRANSACTIONS,
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsTransactions(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForBusinessTransactions = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.BUSINESS_TRANSACTIONS,
  });

  const { parameters: specificParameters, errors: specificErrors } =
    validateParamsBusinessTransactions(commonParameters, getParameter);

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForIncomeCheck = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.INCOME_CHECK,
    scope: 'accounts:read,transactions:read,identity:read',
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsIncomeCheck(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForExpenseCheck = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.EXPENSE_CHECK,
    scope: 'accounts:read,transactions:read,identity:read',
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsIncomeCheck(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForRiskInsights = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.RISK_INSIGHTS,
  });
  const { parameters: specificParameters, errors: specificErrors } = validateParamsRiskInsights(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForProducts = (): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType: ProductType.PRODUCTS,
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsProducts(
    commonParameters,
    getParameter
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

const getValidatedParametersForExtendConsent = (
  productType: ExtendConsentProduct
): ValidatedUrlParameters => {
  const { parameters: commonParameters, errors: commonErrors } = getCommonParameters({
    productType,
  });

  const { parameters: specificParameters, errors: specificErrors } = validateParamsExtendConsent(
    commonParameters,
    getParameter,
    productType
  );

  return {
    parameters: { ...commonParameters, ...specificParameters },
    errors: [...commonErrors, ...specificErrors],
  };
};

export const getValidatedUrlParametersByProductType = (
  productType: ProductType,
  productDimension: ProductDimension
): ValidatedUrlParameters => {
  if (productDimension === ProductDimensionExtendConsent.EXTEND_CONSENT) {
    return getValidatedParametersForExtendConsent(productType as ExtendConsentProduct);
  }

  if (productType === ProductType.AUTHORIZE) {
    return getValidatedParametersForAuthorize();
  }

  if (productType === ProductType.REPORTS) {
    return getValidatedParametersForReports();
  }

  if (productType === ProductType.PAY) {
    if (
      productDimension === ProductDimensionPay.VRP_MANDATE ||
      productDimension === ProductDimensionPay.VRP_MANDATE_PICKUP
    ) {
      return getValidatedParametersForVrpMandate();
    }

    return getValidatedParametersForPayment();
  }

  if (productType === ProductType.CREDENTIALS) {
    return getValidatedParametersForCredentials();
  }

  if (productType === ProductType.ACCOUNT_CHECK) {
    return getValidatedParametersForAccountCheck();
  }

  if (productType === ProductType.BUSINESS_ACCOUNT_CHECK) {
    return getValidatedParametersForBusinessAccountCheck();
  }

  if (productType === ProductType.INCOME_CHECK) {
    return getValidatedParametersForIncomeCheck();
  }

  if (productType === ProductType.EXPENSE_CHECK) {
    return getValidatedParametersForExpenseCheck();
  }

  if (productType === ProductType.TRANSACTIONS) {
    return getValidatedParametersForTransactions();
  }

  if (productType === ProductType.BUSINESS_TRANSACTIONS) {
    return getValidatedParametersForBusinessTransactions();
  }

  if (productType === ProductType.RISK_INSIGHTS) {
    return getValidatedParametersForRiskInsights();
  }

  if (productType === ProductType.PRODUCTS) {
    return getValidatedParametersForProducts();
  }

  if (productType === ProductType.ANY) {
    return {
      parameters: { trackingId: TRACKING_ID },
      errors: [] as CommonError[],
    } as ValidatedUrlParameters;
  }

  throw Error(`Invalid product type provided: ${productType}`);
};

export const getValidatedUrlParametersByProductTypeRedirect = (
  productType: ProductType,
  productDimension: ProductDimension
) => {
  const { parameters, errors } = getValidatedUrlParametersByProductType(
    productType,
    productDimension
  );

  if (!parameters.credentialsIdFromTPP) {
    errors.push({
      errorStatus: ErrorStatus.BAD_REQUEST,
      errorReason: ErrorReason.INVALID_STATE_REDIRECTED_CREDENTIALS_ID,
    });
  }

  if (!parameters.authorizationToken) {
    errors.push({
      errorStatus: ErrorStatus.BAD_REQUEST,
      errorReason: ErrorReason.INVALID_STATE_REDIRECTED_AUTHORIZATION_TOKEN,
    });
  }

  return { parameters, errors };
};

export const buildSafeUrl = (sensitiveUserInformationId = uuid()): string => {
  const { search, pathname } = window.location;
  const params = queryString.parse(search);
  let picked = {};

  if (params.input_username) {
    picked = { input_username: params.input_username };
  }

  availableStorage.setItem(
    SESSION_PROPS_KEY,
    JSON.stringify({ [sensitiveUserInformationId]: picked })
  );

  SENSITIVE_QUERY_PARAMETER_KEYS.forEach((prop) => {
    params[prop] = null;
  });

  params.sui_id = sensitiveUserInformationId;

  return `${pathname}?${queryString.stringify(params, { skipNull: true })}`;
};

export const hasSensitiveParamsInUrl = (): boolean => {
  const { search } = window.location;
  const params = queryString.parse(search);
  const found = Object.keys(params).find((prop) => SENSITIVE_QUERY_PARAMETER_KEYS.includes(prop));

  return Boolean(found);
};
